/* ⚠️ GENERATED from app/services/avatarBody.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarBody.ts — the body: eleven proportions, and where they come from.
//
// 🟥 ONE "BUILD" SLIDER IS NOT A BODY. `slim…heavy` moves every part of the
//    figure at once, so a person with broad shoulders and a narrow waist can
//    only ask to be uniformly bigger or uniformly smaller. Nobody is uniform.
//    The catalogue ships each of these as a morph target pair on the body — and
//    on the clothes, because a shirt that does not follow a broader chest is
//    worse than no shirt — so a build is a vector of −1…1 numbers.
//
// 🟢 THE SAME TWO SOURCES AS THE FACE. The vision model is asked for these when
//    it reads a selfie, and the builder exposes six of them as sliders, so what
//    the model got wrong is one tap from right. `build` (the old single slider)
//    stays: it is the overall size, and these are the proportions on top of it.
//
// 🟥 AND THE MORPHS ARE NAMED `f_<key>_<lo|hi>`, THE SAME AS THE FACE'S. That is
//    not sloppiness — the generator writes both with one function, and the
//    assembler bakes both with one pass, because a body proportion is exactly as
//    permanent as a nose. Two names for one mechanism would only be two bugs.
//
// 🟢 PURE. A twin is generated for the web; the server imports the same list.

/** Every body dimension, with the two words that describe its ends. The words
 *  are the vocabulary the vision model is given — they are what makes a number
 *  mean something to it. */
export const BODY_DIMS = [{
  key: 'shoulders',
  lo: 'narrow sloping shoulders',
  hi: 'broad square shoulders',
  group: 'frame'
}, {
  key: 'chest',
  lo: 'flat narrow chest',
  hi: 'deep broad chest',
  group: 'frame'
}, {
  key: 'waist',
  lo: 'narrow waist',
  hi: 'thick waist',
  group: 'frame'
}, {
  key: 'hips',
  lo: 'narrow hips',
  hi: 'wide hips',
  group: 'frame'
}, {
  key: 'vshape',
  lo: 'straight up-and-down torso',
  hi: 'strongly V-shaped torso',
  group: 'frame'
}, {
  key: 'muscle',
  lo: 'soft untrained build',
  hi: 'defined muscular build',
  group: 'tone'
}, {
  key: 'belly',
  lo: 'flat stomach',
  hi: 'round belly',
  group: 'tone'
}, {
  key: 'buttocks',
  lo: 'flat seat',
  hi: 'full seat',
  group: 'tone'
}, {
  key: 'arms',
  lo: 'slender arms',
  hi: 'thick arms',
  group: 'limbs'
}, {
  key: 'legs',
  lo: 'slender legs',
  hi: 'thick legs',
  group: 'limbs'
}, {
  key: 'neck',
  lo: 'slender neck',
  hi: 'thick neck',
  group: 'limbs'
}];
export const BODY_KEYS = BODY_DIMS.map(d => d.key);

/** The handful the builder shows, in the order people reach for them. */
export const BODY_SLIDERS = ['shoulders', 'chest', 'waist', 'muscle', 'belly', 'legs'];

/** The morph target a dimension drives, given its value. Same shape as the
 *  face's, because the catalogue writes them with the same generator. */
export function bodyMorphName(key, value) {
  return `f_${key}_${value < 0 ? 'lo' : 'hi'}`;
}
const clamp1 = v => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n < -1 ? -1 : n > 1 ? 1 : n;
};

/**
 * A body vector from anything: unknown keys dropped, every value clamped to
 * −1…1, and — 🟥 NEVER `undefined` AND NEVER A ZERO WE DID NOT MEAN. A zero is
 * dropped rather than stored, so "no opinion" and "exactly average" are the same
 * thing, which they are, and the stored document stays small.
 */
export function normalizeBody(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of BODY_KEYS) {
    const v = clamp1(raw[key]);
    if (v !== 0) out[key] = Math.round(v * 100) / 100;
  }
  return out;
}
export function isNeutralBody(body) {
  return Object.keys(normalizeBody(body)).length === 0;
}

/** A stable string for the key/hash — sorted, so field order never matters. */
export function bodyKeyPart(body) {
  const b = normalizeBody(body);
  return BODY_KEYS.filter(k => b[k]).map(k => `${k}:${b[k]}`).join(',');
}

/** Nudge one dimension, for a slider. */
export function withBodyDim(body, key, value) {
  if (!BODY_KEYS.includes(key)) return normalizeBody(body);
  return normalizeBody({
    ...normalizeBody(body),
    [key]: clamp1(value)
  });
}

/** The schema fragment the server hands the vision model, built from the list
 *  above so the two can never drift. */
export function bodyProperties() {
  const out = {};
  for (const d of BODY_DIMS) {
    out[d.key] = {
      type: 'number',
      description: `−1 = ${d.lo}, 0 = average, 1 = ${d.hi}`
    };
  }
  return out;
}

/** What the model is told about bodies, appended to the face instruction. */
export const BODY_PROMPT = ['Also estimate the build from what is visible: shoulders, chest, waist, hips and the rest, each from -1 to 1 against an average adult of the same sex.', 'If the photo shows only a head, answer 0 for every body dimension rather than guessing from the face.'].join(' ');

/** What came back, checked. Anything missing or out of range is neutral. */
export function parseBodyReading(raw) {
  return normalizeBody(raw);
}
